/* eslint-disable react/jsx-props-no-spreading */
import classNames from 'classnames';
import PropTypes from 'prop-types';

import MuiCard from '@mui/material/Card';

import CardBody from './CardBody';
import CardImage from './CardImage';
import CardLabel from './CardLabel';
import CardText from './CardText';
import CardTitle from './CardTitle';

import { useStyles } from './index.styles';

// Map shadow size to a number.
const mapToShadowSize = (size) => ({
  none: '0px',
  sm: '1px',
  md: '2px',
  lg: '3px'
}[size] || null);

export const DEFAULT_PROPS = {
  body: false,
  color: 'focus00',
  shadow: 'md',
  tag: 'div',
  variant: ''
};

const Card = ({
  body = DEFAULT_PROPS.body,
  color = DEFAULT_PROPS.color,
  shadow = DEFAULT_PROPS.shadow,
  tag = DEFAULT_PROPS.tag,
  variant = DEFAULT_PROPS.variant,
  children,
  className,
  ...props
}) => {
  // A string will be used as the bg color. An array can be used to create text and bg colors.
  const colors = Array.isArray(color) ? color : [null, color];
  const [textColor, bgColor] = colors;
  const shadowSize = mapToShadowSize(shadow);
  const classes = useStyles({ bgColor, shadowSize, textColor });

  return (
    <MuiCard
      className={classNames(
        classes.card,
        classes[`card--${variant}`],
        className
      )}
      component={tag}
      data-testid="card"
      elevation={0}
      {...props}
    >
      {body ? <CardBody>{children}</CardBody> : children}
    </MuiCard>
  );
};

Card.propTypes = {
  /** Wrap children in <CardBody> component to add padding. No padding by default. */
  body: PropTypes.bool,
  /** The content of the component. */
  children: PropTypes.node.isRequired,
  /** Override or extend the styles applied to the component. */
  className: PropTypes.string,
  /** Set background color or text and background color using an array. Uses theme color keys. */
  color: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]),
  /** Set shadow size. Allows for outlined card without a dropshadow. */
  shadow: PropTypes.oneOf(['none', 'sm', 'md', 'lg']),
  /** Change the wrapping elements tag. */
  tag: PropTypes.elementType,
  /** Set style of the card element to include border and box shadow */
  variant: PropTypes.oneOf(['outlined', ''])
};

export default Object.assign(Card, {
  Body: CardBody,
  Image: CardImage,
  Label: CardLabel,
  Text: CardText,
  Title: CardTitle
});
